/**
 * DayDateCell — komórka daty w wierszu listy dni wolnych (Ustawienia → Dni wolne od pracy); nie
 * mylić z komórką dnia siatki kalendarza (`DayCell` wewnątrz `components/calendar/MonthGrid.tsx`).
 *
 * Wyświetlanie jako tekst („25.12" / „29.03.2027"); klik albo Enter/F2/spacja przełącza w edycję
 * przez `DatePicker` otwarty od razu (`defaultOpen`). Wybór dnia zapisuje od razu (kolizja → błąd pod
 * polem, bez zapisu); klik poza komórką albo Esc anuluje edycję (bez żądania do API), a po wyjściu z
 * edycji fokus wraca na komórkę (poza kliknięciem poza nią — wtedy fokus należy do klikniętego
 * elementu). W trakcie zapisu klik poza komórką i Esc nie zamykają edycji (jak okno w trakcie
 * zadania) — komórka czeka na odpowiedź serwera, więc wynik (zamknięcie albo błąd kolizji) zawsze
 * trafia do użytkownika.
 *
 * Nie używa `InlineEditableCell`, bo ten zapisuje na blur/Enter z roboczej wartości, a `DatePicker`
 * (popover w portalu, bez onBlur/onKeyDown) zapisuje przy wyborze dnia.
 */

import { type ReactElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DATE_PICKER_PANEL_SELECTOR, DatePicker } from '@/components/DatePicker';
import type { NonWorkingDayDto } from '@/lib/non-working-days/api';

import { formatDayLabel } from './non-working-day-utils';

export interface DayDateCellProps {
  entry: NonWorkingDayDto;
  /**
   * Zwraca komunikat błędu do pokazania pod polem (komórka zostaje w edycji) albo `null`, gdy
   * komórka może się zamknąć: zapisano, albo błąd inny niż kolizja został zgłoszony toastem —
   * wiersz pokazuje wtedy wartość z serwera.
   */
  onCommit: (day: string) => Promise<string | null>;
  /** Błąd z innej akcji w tym wierszu (np. zaznaczenie „Co roku" kolidujące z innym wpisem). */
  externalError: string | null;
}

export function DayDateCell({ entry, onCommit, externalError }: DayDateCellProps): ReactElement {
  const { t } = useTranslation();
  const errorId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.day);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const displayRef = useRef<HTMLSpanElement | null>(null);
  // Czy po zamknięciu edycji oddać fokus komórce (patrz efekt niżej) — ustawiane przez `stopEdit`.
  const restoreFocusRef = useRef(false);

  const startEdit = (): void => {
    setDraft(entry.day);
    setError(null);
    setEditing(true);
  };

  const stopEdit = useCallback((restoreFocus: boolean): void => {
    restoreFocusRef.current = restoreFocus;
    setEditing(false);
    setError(null);
  }, []);

  // Po wyjściu z edycji (Esc / zapis) komórka-tekst montuje się od nowa, a panel kalendarza (który
  // trzymał fokus) znika — bez tego fokus spadałby na <body>.
  useEffect(() => {
    if (editing || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    displayRef.current?.focus();
  }, [editing]);

  // Klik poza komórką anuluje edycję. Panel `DatePicker` jest w portalu poza komórką — klik w niego
  // (np. w nawigację miesiąca) to nadal edycja; rozpoznajemy go po publicznym znaczniku panelu.
  // W trakcie zapisu nasłuchu nie ma — edycja czeka na odpowiedź serwera.
  useEffect(() => {
    if (!editing || saving) return;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (wrapperRef.current?.contains(target)) return;
      if (target.closest(DATE_PICKER_PANEL_SELECTOR)) return;
      stopEdit(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [editing, saving, stopEdit]);

  const handlePick = async (value: string): Promise<void> => {
    setDraft(value);
    if (value === '') {
      setError(t('tenant.nonWorkingDays.dateRequired'));
      return;
    }
    if (value === entry.day) {
      stopEdit(true);
      return;
    }
    setSaving(true);
    const message = await onCommit(value);
    setSaving(false);
    if (message !== null) {
      setError(message);
      return;
    }
    stopEdit(true);
  };

  if (!editing) {
    return (
      <div>
        <span
          ref={displayRef}
          role="button"
          tabIndex={0}
          onClick={startEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'F2' || e.key === ' ') {
              e.preventDefault();
              startEdit();
            }
          }}
          className="-mx-2 block min-h-7 cursor-text rounded px-2 py-1 tabular-nums text-text-primary hover:bg-bg-tertiary"
          data-testid={`non-working-day-date-${entry.id}`}
        >
          {formatDayLabel(entry.day, entry.repeatsYearly)}
        </span>
        {externalError !== null && (
          <p
            role="alert"
            className="mt-0.5 text-xs text-red-600 dark:text-red-400"
            data-testid={`non-working-day-date-error-${entry.id}`}
          >
            {externalError}
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !saving) stopEdit(true);
      }}
    >
      <DatePicker
        value={draft}
        onChange={(value) => void handlePick(value)}
        defaultOpen
        disabled={saving}
        invalid={error !== null}
        className="w-44"
        aria-label={t('tenant.nonWorkingDays.col.date')}
        aria-describedby={error !== null ? errorId : undefined}
        data-testid={`non-working-day-date-picker-${entry.id}`}
      />
      {error !== null && (
        <p
          id={errorId}
          role="alert"
          className="mt-0.5 text-xs text-red-600 dark:text-red-400"
          data-testid={`non-working-day-date-error-${entry.id}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}
